import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useLogout } from './useLogout';
import { useTheme } from '../contexts';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import {
  useListPortfoliosQuery,
  useLazyGetDefaultPortfolioQuery,
  useListPortfolioSitesQuery,
  useListAdminSitesQuery,
  useLazyGetPortfolioSiteQuery,
} from '../redux/api/portfolioApi';
import type { Portfolio, PortfolioSite } from '../redux/api/portfolioApi';
import { useListQAResultsNewQuery, type QAResultNew } from '../redux/api/qaApi';
import { setSelectedPortfolioId } from '../redux/slices/portfolioSlice';
import { setCurrentSite, setSitesForPortfolio } from '../redux/slices/siteSlice';
import type { TenantInfo } from '../redux/slices/authSlice';
import { Share2, Settings, Upload, ChevronLeft, ChevronRight, User, ShieldCheck, ChevronDown, Tag, LogOut, BarChart3, List, FileCheck, Table2, FileText, GitCompare, CircleHelp, ClipboardList, BookOpen, Calendar, LineChart, FolderOpen, LayoutDashboard, Map } from 'lucide-react';

export interface TabDef {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  description: string;
}

export interface UseSidebarReturn {
  // Theme
  resolvedTheme: string;

  // Auth & permissions
  canUpload: boolean;
  showAdminButton: boolean;
  adminLabel: string;
  isSuperAdmin: boolean;

  // Portfolio
  portfolios: Portfolio[];
  selectedPortfolioId: string | null;
  handlePortfolioSelect: (portfolioId: string | null) => void;

  // Sites
  currentSite: PortfolioSite | null;
  portfolioSites: PortfolioSite[];
  sitesLoading: boolean;
  hasQAResultsForSite: boolean;

  // Section toggles
  portfolioTabsOpen: boolean;
  togglePortfolioTabs: () => void;
  siteTabsOpen: boolean;
  toggleSiteTabs: () => void;
  qaTabsOpen: boolean;
  toggleQaTabs: () => void;
  settingsOpen: boolean;
  toggleSettings: () => void;

  // Site dropdown
  isDropdownOpen: boolean;
  setIsDropdownOpen: (open: boolean) => void;
  siteSearchTerm: string;
  setSiteSearchTerm: (term: string) => void;
  dropdownPosition: { top: number; left: number; width: number } | null;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  siteSelectorButtonRef: React.RefObject<HTMLButtonElement | null>;
  siteDropdownRef: React.RefObject<HTMLDivElement | null>;
  handleSiteSelect: (site: PortfolioSite) => Promise<void>;

  // Upload
  createSiteLoading: boolean;
  handleCreateSiteWithAsBuilt: () => void;

  // Logout
  showLogoutDialog: boolean;
  setShowLogoutDialog: (show: boolean) => void;
  handleLogout: () => Promise<void>;
  isLoggingOut: boolean;

  // Tab definitions
  tabs: TabDef[];
  qaTabsAlways: TabDef[];
  qaTabsWhenResults: TabDef[];
}

export function useSidebar(onAddTab: (tabId: string) => void): UseSidebarReturn {
  const { resolvedTheme } = useTheme();
  const { handleLogout, isLoading: isLoggingOut } = useLogout();
  const dispatch = useAppDispatch();

  // --- State ---
  const [createSiteLoading, setCreateSiteLoading] = useState(false);
  const [portfolioTabsOpen, setPortfolioTabsOpen] = useState(true);
  const [siteTabsOpen, setSiteTabsOpen] = useState(true);
  const [qaTabsOpen, setQaTabsOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // --- Refs ---
  const dropdownRef = useRef<HTMLDivElement>(null);
  const siteSelectorButtonRef = useRef<HTMLButtonElement>(null);
  const siteDropdownRef = useRef<HTMLDivElement>(null);

  // --- Redux selectors ---
  const profile = useAppSelector((state) => state.auth.profile);
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const selectedPortfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);
  const organizationState = useAppSelector((s) => s.organization);

  // --- Derived auth values ---
  const userEmail = (profile.email || '').toLowerCase();
  const isPlatformSuperAdmin = userEmail.endsWith('@engramiq.com');
  const canUpload = isPlatformSuperAdmin || profile.can_upload === true;

  const activeTenant = (profile as any).active_tenant as TenantInfo | null | undefined;
  const activeTenantRole = activeTenant?.role;

  // Only show admin for super admins for now
  const showAdminButton = isPlatformSuperAdmin;
  const adminLabel = isPlatformSuperAdmin ? 'Super Admin' : 'Admin';
  const isSuperAdmin = isPlatformSuperAdmin;

  // --- API queries ---

  // QA results for current site
  const { data: qaResultsData } = useListQAResultsNewQuery(
    currentSite?.site_id ? { site_id: currentSite.site_id, limit: 100 } : undefined,
    { skip: !currentSite?.site_id }
  );
  const hasQAResultsForSite = qaResultsData?.items?.some(
    (result: QAResultNew) => result.status === 'completed'
  ) ?? false;

  // Portfolios — always fetch for all users (super admins need them for the picker)
  const { data: portfoliosResponse, isLoading: portfoliosLoading } = useListPortfoliosQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const portfolios = portfoliosResponse?.items || [];

  // Super admin organization context
  const currentOrgId = isSuperAdmin ? organizationState.currentOrganization?.id ?? null : null;

  // Whether to use portfolio-scoped sites (when a portfolio is explicitly selected)
  const usePortfolioScoping = !!selectedPortfolioId;

  // Admin sites — fallback for super admins when no portfolio selected
  const adminArgs = isSuperAdmin
    ? (currentOrgId ? { organization_id: currentOrgId } : undefined)
    : undefined;

  const {
    data: adminSitesResponse,
    isLoading: adminSitesLoading,
  } = useListAdminSitesQuery(adminArgs, {
    skip: !isSuperAdmin || usePortfolioScoping,
  });

  // Portfolio sites — used when a portfolio is selected (for ALL users including super admins)
  const { data: portfolioSitesResponse, isLoading: portfolioSitesLoading } = useListPortfolioSitesQuery(
    selectedPortfolioId
      ? { portfolio_id: selectedPortfolioId }
      : { portfolio_id: '' },
    {
      skip: !selectedPortfolioId,
    }
  );

  // Combine sites: portfolio takes priority, then admin sites for super admins
  const allSites = usePortfolioScoping
    ? (Array.isArray(portfolioSitesResponse)
      ? portfolioSitesResponse
      : (portfolioSitesResponse as any)?.items || []) as PortfolioSite[]
    : isSuperAdmin
      ? (Array.isArray(adminSitesResponse)
        ? adminSitesResponse
        : (adminSitesResponse as any)?.items || []) as PortfolioSite[]
      : (Array.isArray(portfolioSitesResponse)
        ? portfolioSitesResponse
        : (portfolioSitesResponse as any)?.items || []) as PortfolioSite[];

  const portfolioSites = allSites;
  const sitesLoading = usePortfolioScoping
    ? portfolioSitesLoading
    : (isSuperAdmin ? adminSitesLoading : portfolioSitesLoading);

  const [triggerGetPortfolioSite] = useLazyGetPortfolioSiteQuery();
  const [triggerGetDefaultPortfolio] = useLazyGetDefaultPortfolioQuery();

  // --- Effects ---

  // Position dropdown when it opens (for portal)
  useLayoutEffect(() => {
    if (isDropdownOpen && siteSelectorButtonRef.current) {
      const rect = siteSelectorButtonRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    } else {
      setDropdownPosition(null);
    }
  }, [isDropdownOpen]);

  // Click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = dropdownRef.current?.contains(target);
      const inDropdown = siteDropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        setIsDropdownOpen(false);
        setSiteSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Auto-select first portfolio if none selected (non-super-admin only)
  useEffect(() => {
    if (!isSuperAdmin && !selectedPortfolioId && portfolios.length > 0) {
      dispatch(setSelectedPortfolioId(portfolios[0].id));
    }
  }, [dispatch, isSuperAdmin, selectedPortfolioId, portfolios]);

  // Keep sites list in Redux for selected portfolio
  useEffect(() => {
    if (selectedPortfolioId && portfolioSites.length > 0) {
      dispatch(setSitesForPortfolio({ portfolioId: selectedPortfolioId, sites: portfolioSites }));
    }
  }, [dispatch, selectedPortfolioId, portfolioSites]);

  // --- Handlers ---

  const handlePortfolioSelect = useCallback((portfolioId: string | null) => {
    dispatch(setSelectedPortfolioId(portfolioId));
    // Clear current site when switching portfolios
    dispatch(setCurrentSite(null));
  }, [dispatch]);

  const handleSiteSelect = useCallback(async (site: PortfolioSite) => {
    const portfolioIdToUse = isSuperAdmin
      ? (site.portfolio_id || (site as any).portfolio_id || selectedPortfolioId)
      : selectedPortfolioId;
    if (!portfolioIdToUse) return;
    try {
      const fullSite = await triggerGetPortfolioSite({
        portfolio_id: portfolioIdToUse,
        site_id: site.site_id,
      }).unwrap();
      dispatch(setCurrentSite(fullSite));
      setIsDropdownOpen(false);
      setSiteSearchTerm('');
    } catch (err) {
      console.error('Failed to fetch site details', err);
    }
  }, [isSuperAdmin, selectedPortfolioId, triggerGetPortfolioSite, dispatch]);

  const handleCreateSiteWithAsBuilt = useCallback(() => {
    onAddTab('sitestable');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('triggerUploadAsBuilt', {}));
    }, 100);
  }, [onAddTab]);

  // --- Toggle helpers ---
  const togglePortfolioTabs = useCallback(() => setPortfolioTabsOpen(v => !v), []);
  const toggleSiteTabs = useCallback(() => setSiteTabsOpen(v => !v), []);
  const toggleQaTabs = useCallback(() => setQaTabsOpen(v => !v), []);
  const toggleSettings = useCallback(() => setSettingsOpen(v => !v), []);

  // --- Tab definitions ---
  const tabs: TabDef[] = [
    // { id: 'sitedetails', name: 'Site Details', icon: LayoutDashboard, description: 'Site dashboard overview' },
    { id: 'eventstasks', name: 'Events & Tasks', icon: Calendar, description: 'Events grouped by task with filtering' },
    { id: 'interactivetimeline', name: 'Timeline Analysis', icon: LineChart, description: 'D3 interactive timeline with drill-down grouping' },
    { id: 'documents', name: 'Documents', icon: FolderOpen, description: 'Document tree view with inline preview' },
    // { id: 'extractiondata', name: 'Extraction Report', icon: FileCheck, description: 'Validation results and drawing inconsistencies' },
    { id: 'networkviewer', name: 'Network View', icon: Share2, description: 'Hierarchical component network' },
    { id: 'asbuilt', name: 'As-Built View', icon: FileText, description: 'View as-built drawings and documentation' },
    { id: 'componentlist', name: 'Component View', icon: List, description: 'All components grouped by type' },
    { id: 'sitemap', name: 'Site Map', icon: Map, description: 'Spatial layers and 3D site visualization' },
  ];

  const qaTabsAlways: TabDef[] = [
    { id: 'questionsets', name: 'Question Set', icon: CircleHelp, description: 'View and manage questions' },
    { id: 'qaexecutivesummary', name: hasQAResultsForSite ? 'Executive Summary' : 'Run QA Analysis', icon: BarChart3, description: hasQAResultsForSite ? 'View executive summary and analytics for QA results' : 'Run QA analysis on as-built drawings' },
  ];

  const qaTabsWhenResults: TabDef[] = [
    { id: 'pagelist', name: 'Page List', icon: FileText, description: 'View pages with sheet information and QA statistics' },
    { id: 'questionsummary', name: 'Answers Summary', icon: ClipboardList, description: 'View paginated list of answers' },
    { id: 'nomenclature', name: 'Nomenclature Validation', icon: Tag, description: 'Equipment naming and labeling deviations' },
    { id: 'crosspage', name: 'Cross-Page Validation', icon: GitCompare, description: 'Consistency checks across multiple pages' },
    { id: 'pagereferences', name: 'Page References', icon: BookOpen, description: 'Page reference validation with valid/invalid status' },
  ];

  return {
    resolvedTheme,
    canUpload,
    showAdminButton,
    adminLabel,
    isSuperAdmin,
    portfolios,
    selectedPortfolioId,
    handlePortfolioSelect,
    currentSite,
    portfolioSites,
    sitesLoading,
    hasQAResultsForSite,
    portfolioTabsOpen,
    togglePortfolioTabs,
    siteTabsOpen,
    toggleSiteTabs,
    qaTabsOpen,
    toggleQaTabs,
    settingsOpen,
    toggleSettings,
    isDropdownOpen,
    setIsDropdownOpen,
    siteSearchTerm,
    setSiteSearchTerm,
    dropdownPosition,
    dropdownRef,
    siteSelectorButtonRef,
    siteDropdownRef,
    handleSiteSelect,
    createSiteLoading,
    handleCreateSiteWithAsBuilt,
    showLogoutDialog,
    setShowLogoutDialog,
    handleLogout,
    isLoggingOut,
    tabs,
    qaTabsAlways,
    qaTabsWhenResults,
  };
}
